import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { PageHero, Reveal, SectionHead, Tag, EmptyState, EASE } from "@/components/Shared";
import { useCollection, fmtDate, IMG } from "@/lib/api";

const CAT_COLORS = {
  Project: "#FFFFFF",
  GBM: "#C0C9D6",
  "District Event": "#7A93C4",
  Competition: "#D8B65C",
  Fellowship: "#B57BD8",
  Deadline: "#94A3B8",
  Publication: "#6FA8A0",
  "Special Event": "#E2E8F0",
};

const pad = (n) => String(n).padStart(2, "0");

export default function CalendarPage() {
  const { items, loading } = useCollection("events");
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(null);

  const upcoming = useMemo(
    () =>
      (items || [])
        .filter((e) => e.status === "upcoming")
        .sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [items]
  );

  useEffect(() => {
    if (!cursor && upcoming.length > 0) {
      const [y, m] = upcoming[0].date.split("-").map(Number);
      setCursor(new Date(y, m - 1, 1));
    }
  }, [upcoming, cursor]);

  if (!cursor && !loading) setCursor(new Date(2026, 9, 1));

  const year = cursor ? cursor.getFullYear() : 2026;
  const month = cursor ? cursor.getMonth() : 9;
  const monthLabel = cursor ? cursor.toLocaleString("en-IN", { month: "long", year: "numeric" }) : "…";

  const byDay = {};
  (items || []).forEach((e) => {
    if (!e.date) return;
    (byDay[e.date] = byDay[e.date] || []).push(e);
  });

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => `${year}-${pad(month + 1)}-${pad(i + 1)}`),
  ];

  const move = (dir) => setCursor(new Date(year, month + dir, 1));
  const dayEvents = selected ? byDay[selected] || [] : [];

  return (
    <main>
      <PageHero eyebrow="RCSCW Calendar" title="The year," italic="mapped out" image={IMG.celestialSky}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Projects, GBMs, district events, competitions, fellowships, deadlines and publications — click any marked
          date to see its details.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="calendar-section">
        <Reveal>
          <div className="card-line p-6 sm:p-10" data-testid="calendar-grid">
            <div className="flex items-center justify-between">
              <button onClick={() => move(-1)} className="flex h-10 w-10 items-center justify-center border border-silver/20 text-silver-dim transition-colors hover:border-silver/60 hover:text-white" data-testid="calendar-prev-btn" aria-label="Previous month">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center">
                <p className="eyebrow">Rotary Year 2026–27</p>
                <h2 className="mt-2 font-display text-3xl font-semibold metallic sm:text-4xl">{monthLabel}</h2>
              </div>
              <button onClick={() => move(1)} className="flex h-10 w-10 items-center justify-center border border-silver/20 text-silver-dim transition-colors hover:border-silver/60 hover:text-white" data-testid="calendar-next-btn" aria-label="Next month">
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="mt-8 grid grid-cols-7 gap-px border border-silver/10 bg-silver/10 text-center" data-testid="calendar-days">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="bg-navy/80 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-silver-muted">
                  {d}
                </div>
              ))}
              {cells.map((date, i) => {
                if (!date) return <div key={`empty-${i}`} className="min-h-[72px] bg-ink/40" />;
                const evts = byDay[date] || [];
                const dayNum = Number(date.slice(8));
                const has = evts.length > 0;
                return (
                  <button
                    key={date}
                    onClick={() => has && setSelected(date)}
                    className={`relative min-h-[72px] bg-ink/60 p-2 text-left align-top transition-colors ${
                      has ? "cursor-pointer hover:bg-navy-light/60" : "cursor-default"
                    } ${selected === date ? "bg-navy-light/80" : ""}`}
                    data-testid={`calendar-day-${date}`}
                  >
                    <span className={`font-mono text-xs ${has ? "text-silver" : "text-silver-muted/60"}`}>{dayNum}</span>
                    {has && (
                      <span className="absolute bottom-2 left-2 flex gap-1">
                        {[...new Set(evts.map((e) => e.category))].slice(0, 3).map((c) => (
                          <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ background: CAT_COLORS[c] || "#E2E8F0" }} />
                        ))}
                      </span>
                    )}
                    {has && <span className="absolute right-2 top-2 h-1 w-1 rounded-full bg-ember animate-pulse-soft" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2" data-testid="calendar-legend">
              {Object.entries(CAT_COLORS).map(([c, color]) => (
                <span key={c} className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.18em] text-silver-muted">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} /> {c}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-16">
          <SectionHead eyebrow="This Month & Beyond" title="Upcoming" italic="events" />
          <div className="mt-10 divide-y divide-silver/10 border-y border-silver/10">
            {upcoming.slice(0, 8).map((e, i) => (
              <Reveal key={e.id} delay={i * 0.05}>
                <button onClick={() => { setCursor(new Date(+e.date.slice(0, 4), +e.date.slice(5, 7) - 1, 1)); setSelected(e.date); }} className="group flex w-full flex-wrap items-center gap-6 py-5 text-left" data-testid={`calendar-event-row-${e.id}`}>
                  <span className="w-40 font-mono text-xs tracking-[0.15em] text-ember">{fmtDate(e.date)}</span>
                  <span className="flex-1 font-display text-2xl text-silver transition-colors group-hover:text-white">{e.title}</span>
                  <Tag>{e.category}</Tag>
                </button>
              </Reveal>
            ))}
            {!loading && upcoming.length === 0 && (
              <div className="mt-8"><EmptyState title="No upcoming events yet" note="Events added via the Admin panel appear on the calendar instantly." /></div>
            )}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-[95] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="calendar-event-modal"
          >
            <motion.div
              className="card-line relative w-full max-w-lg p-8"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setSelected(null)} className="absolute right-4 top-4 text-silver-muted hover:text-white" aria-label="Close event" data-testid="calendar-modal-close-btn">
                <X size={20} />
              </button>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember">{fmtDate(selected)}</p>
              {dayEvents.length === 0 && <p className="mt-6 text-sm text-silver-muted">No events on this date.</p>}
              {dayEvents.map((e) => (
                <div key={e.id} className="mt-6 border-t border-silver/10 pt-6 first:border-0 first:pt-0" data-testid="calendar-event-detail">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full" style={{ background: CAT_COLORS[e.category] || "#E2E8F0" }} />
                    <Tag>{e.category}</Tag>
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-semibold text-silver">{e.title}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                    {e.time || "TBA"} · {e.venue || "TBA"}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-silver-dim">{e.description}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
